import styled from '@emotion/styled';
import {
  DSButton,
  DSCombobox,
  DSField,
  DSLandingPage,
  DSSelectMultiple,
} from '@hundred5/design-system';
import { differenceInMinutes } from 'date-fns/differenceInMinutes';
import { formatDistanceStrict } from 'date-fns/formatDistanceStrict';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { debounce } from 'lodash';
import { usePostHog } from 'posthog-js/react';
import React, { useEffect, useRef, useState } from 'react';

import { getAmplitudeClient } from '@/features/amplitude/utils/amplitude';
import {
  FormikInputField,
  FormikRadioField,
  FormikSelectField,
  Icon,
  PromptIfDirty,
  usePageLayout,
} from '@/features/common';
import {
  createEditorState,
  CustomTextEditor,
  getEditorMarkdown,
} from '@/features/common/components/custom-text-editor';
import {
  CURRENCY_OPTIONS,
  EMPLOYMENT_TYPE_OPTIONS,
  REMOTE_LOCATION_OPTIONS,
  TWorkplaceType,
  validationJobDescriptionSchema,
} from '@/features/job-opening';
import { useNotifications } from '@/features/notifications';
import { useJobOpeningPermission } from '@/features/permissions';
import { useUpdateWorkspaceMutation, useWorkspace } from '@/features/workspace';

import { getPlacePredictions } from '../../api/google-maps-locations';

import { useJobOpeningId } from '@/hooks/router';
import { usePatchJobopeningDescriptionMutation } from '@/queries/jobopenings/mutations';
import { useGetJobopeningDescription } from '@/queries/jobopenings/queries';
import { WorkplaceType } from '@/queries/types';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { EditorActions, Logo, TitleImage } from './ui';

import googleOnWhite from './assets/google_on_white.png';

type TInitialValues = {
  backgroundUrl: string;
  backgroundPosition: { x: number; y: number };
  logoUrl: string;
  jobTitle: string;
  employmentType: string;
  department: string;
  workplaceType: WorkplaceType | null;
  description: string;
  salaryCurrency: string;
  salaryMin: number | null;
  salaryMax: number | null;
  locationAreas: string[];
  locationCity: string;
};

interface IComboboxItem {
  id: string;
  label: string;
  value: string;
}

export function JobOpeningDescription() {
  const { addNotification } = useNotifications();
  const canUpdate = useJobOpeningPermission()('job opening', 'update');
  const amplitude = getAmplitudeClient();
  const posthog = usePostHog();
  const jobOpeningId = useJobOpeningId();
  const workspace = useWorkspace();

  const {
    data: jobOpeningDescription,
    isRefetching: jobOpeningDescriptionIsRefetching,
  } = useGetJobopeningDescription(
    Number(workspace?.id!),
    Number(jobOpeningId!),
    true
  );

  const offsetTopBanner = usePageLayout().offsetTop;

  const initialValuesRef = useRef<null | TInitialValues>(null);
  const [editorState, setEditorState] = useState(createEditorState(''));
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const [backgroundId, setBackgroundId] = useState<string | undefined>(
    undefined
  );

  const updateWorkspaceMutation = useUpdateWorkspaceMutation({});
  const updateJobOpeningDescription = usePatchJobopeningDescriptionMutation({
    meta: { invalidates: 'all' },
  });

  const lastSavedTimeDistance = jobOpeningDescription?.updatedAt
    ? differenceInMinutes(Date.now(), jobOpeningDescription.updatedAt) < 1
      ? 'less than a minute'
      : formatDistanceStrict(jobOpeningDescription.updatedAt, Date.now())
    : undefined;

  const [locationItems, setLocationItems] = useState<string[]>([]);
  const [comboboxItems, setComboboxItems] = useState<IComboboxItem[]>([]);

  // Set the initial values only once, when the form is first loaded
  useEffect(() => {
    if (
      jobOpeningDescription &&
      !jobOpeningDescriptionIsRefetching &&
      workspace &&
      !initialValuesRef.current
    ) {
      initialValuesRef.current = {
        backgroundUrl: jobOpeningDescription.backgroundUrl ?? '',
        backgroundPosition: jobOpeningDescription.backgroundPosition ?? {
          x: 0,
          y: 0,
        },
        logoUrl: workspace.logo_url,
        jobTitle: jobOpeningDescription.name,
        employmentType:
          jobOpeningDescription.employmentType ??
          EMPLOYMENT_TYPE_OPTIONS[0].value,
        department: jobOpeningDescription.department ?? '',
        workplaceType: jobOpeningDescription.workplaceType ?? null,
        description: jobOpeningDescription.description ?? '',
        locationCity: jobOpeningDescription.locationCity ?? '',
        locationAreas: jobOpeningDescription.locationAreas ?? [],
        salaryCurrency:
          jobOpeningDescription.salaryCurrency ?? CURRENCY_OPTIONS[0].value,
        salaryMin: jobOpeningDescription.salaryMin ?? null,
        salaryMax: jobOpeningDescription.salaryMax ?? null,
      };

      setLocationItems(jobOpeningDescription.locationAreas ?? []);
      setComboboxItems([
        {
          id: 'default',
          label: jobOpeningDescription.locationCity ?? '',
          value: jobOpeningDescription.locationCity ?? '',
        },
      ]);
      setLocationId(jobOpeningDescription.locationCity ? 'default' : undefined);

      if (!!jobOpeningDescription.description) {
        setEditorState(createEditorState(jobOpeningDescription.description));
      }
    }
  }, [jobOpeningDescription, jobOpeningDescriptionIsRefetching, workspace]);

  // Debounced fetch and set
  const debouncedFetch = useRef(
    debounce(
      async (
        query: string,
        callback: (results: IComboboxItem[]) => void,
        errorCallback: (error: any) => void
      ) => {
        try {
          const results = await getPlacePredictions(query);
          callback(results);
        } catch (error) {
          errorCallback(error);
        }
      },
      400
    )
  ).current;

  // onSearchChange returns a promise for DSCombobox
  const onSearchChange = (query: string) => {
    return new Promise<IComboboxItem[]>((resolve) => {
      debouncedFetch(
        query,
        (results) => {
          if (Array.isArray(results)) {
            setComboboxItems(results);
            resolve(results);
          } else {
            setComboboxItems([]);
            resolve([]);
          }
        },
        (error) => {
          // TODO: handle error with toast
          setComboboxItems([]);
          resolve([]);
          throw error;
        }
      );
    });
  };

  if (!workspace || !jobOpeningDescription || !initialValuesRef.current) {
    return null;
  }

  const handleSubmit = async (values: any, helpers: FormikHelpers<any>) => {
    updateJobOpeningDescription.mutate(
      {
        workspaceId: Number(workspace.id),
        openingId: Number(jobOpeningId),
        data: {
          name: values.jobTitle,
          description: values.description,
          employmentType: values.employmentType,
          department: values.department,
          workplaceType: values.workplaceType,
          backgroundId: backgroundId,
          backgroundPosition: values.backgroundPosition,
          locationId: locationId,
          locationCity:
            values.workplaceType !== TWorkplaceType.Remote
              ? values.locationCity
              : '',
          locationAreas:
            values.workplaceType === TWorkplaceType.Remote
              ? values.locationAreas
              : [],
          salaryCurrency: values.salaryCurrency,
          salaryMin: !!values.salaryMin ? values.salaryMin : null,
          salaryMax: !!values.salaryMax ? values.salaryMax : null,
        },
      },
      {
        onSuccess: () => {
          addNotification({ type: 'saved' });
        },
      }
    );

    await updateWorkspaceMutation.mutateAsync({
      workspaceId: String(workspace.id),
      workspace: {
        logoUrl: values.logo_url,
        gmailEnabled: workspace.gmail_enabled,
        name: workspace.name,
        company: workspace.company,
      },
    });

    // Reset the form and update initial values
    initialValuesRef.current = values;
    helpers.resetForm({ values });
    setLocationId(undefined);

    amplitude?.logEvent('job description/save', {
      'workspace id': workspace.id,
      'job opening id': jobOpeningId,
    });
    posthog?.capture('hire_admin_app:job description/save', {
      'workspace id': workspace.id,
      'job opening id': jobOpeningId,
    });
  };

  const ScrollToFirstError = ({ submitCount, errors }) => {
    const { isValid, isSubmitting } = useFormikContext();

    useEffect(() => {
      if (submitCount > 0 && !isValid && !isSubmitting) {
        const errorElement = document.querySelector('[data-has-error="true"]');

        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }
    }, [submitCount, isValid, isSubmitting, errors]);

    return null;
  };

  return (
    <Formik
      initialValues={initialValuesRef.current}
      validationSchema={validationJobDescriptionSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({
        values,
        touched,
        errors,
        dirty,
        isSubmitting,
        setFieldValue,
        submitCount,
      }) => {
        return (
          <StyledForm>
            <PromptIfDirty />
            <ScrollToFirstError submitCount={submitCount} errors={errors} />
            <Container>
              <HeaderWrapper offsetTopBanner={offsetTopBanner}>
                {lastSavedTimeDistance && (
                  <SaveInfo>Last saved {lastSavedTimeDistance} ago</SaveInfo>
                )}

                <SaveButton
                  variant="secondary"
                  type="submit"
                  disabled={!canUpdate || !dirty || isSubmitting}
                  data-rh={!canUpdate ? 'Only admins can save changes' : null}
                >
                  {isSubmitting ? 'Saving...' : 'Save changes'}
                </SaveButton>
              </HeaderWrapper>

              <DSLandingPage embedded>
                <TitleImage
                  editable
                  url={values.backgroundUrl}
                  position={values.backgroundPosition}
                  onUpdate={(imageData) => {
                    setFieldValue('backgroundUrl', imageData?.url ?? '');
                    setBackgroundId(imageData?.id ?? '');
                  }}
                  onReposition={(position) => {
                    setFieldValue('backgroundPosition', position);
                  }}
                />

                <DSLandingPage.Header>
                  <Logo
                    url={values.logo_url}
                    onUpdate={(url) => {
                      setFieldValue('logoUrl', url);
                    }}
                  />
                </DSLandingPage.Header>

                <DSLandingPage.Main>
                  <FormikInputField
                    name="jobTitle"
                    label="Job title"
                    placeholder="e.g. Frontend Developer"
                    required
                  />

                  <ColumnWrapper>
                    <InputWrapper>
                      <FormikSelectField
                        name="employmentType"
                        label="Employment type"
                        placeholder="e.g. Full time"
                        items={EMPLOYMENT_TYPE_OPTIONS}
                      />
                    </InputWrapper>
                    <InputWrapper>
                      <FormikInputField
                        name="department"
                        label="Department"
                        placeholder="e.g. Marketing"
                      />
                    </InputWrapper>
                  </ColumnWrapper>

                  <DSField label="Salary" for="salaryCurrency">
                    <SalaryInputWrapper>
                      <FormikSelectField
                        name="salaryCurrency"
                        placeholder="EUR"
                        items={CURRENCY_OPTIONS}
                        topItems={3}
                      />
                      <FormikInputField
                        name="salaryMin"
                        placeholder="Min"
                        type="number"
                      />
                      <FormikInputField
                        name="salaryMax"
                        placeholder="Max"
                        type="number"
                      />
                    </SalaryInputWrapper>
                  </DSField>

                  <SettingsSection>
                    <DSField
                      label="Workplace Type"
                      for="workplaceType"
                      error={
                        touched.workplaceType && !!errors.workplaceType ? (
                          <>{errors.workplaceType as String}</>
                        ) : null
                      }
                    >
                      <OptionsWrapper>
                        <FormikRadioField
                          name="workplaceType"
                          value={TWorkplaceType.Remote}
                          label={
                            <LabelWrapper>
                              Remote
                              <Info>Employees work from home</Info>
                            </LabelWrapper>
                          }
                        />
                        <FormikRadioField
                          name="workplaceType"
                          value={TWorkplaceType.Hybrid}
                          label={
                            <LabelWrapper>
                              Hybrid
                              <Info>
                                Employees work from both office and home
                              </Info>
                            </LabelWrapper>
                          }
                        />
                        <FormikRadioField
                          name="workplaceType"
                          value={TWorkplaceType.Onsite}
                          label={
                            <LabelWrapper>
                              On-site
                              <Info>Employees work from an office</Info>
                            </LabelWrapper>
                          }
                        />
                      </OptionsWrapper>
                    </DSField>
                    {values.workplaceType === TWorkplaceType.Remote && (
                      <DSField
                        label={
                          <InfoWrapper>
                            <span>Employee Location</span>
                            <span
                              data-rh={
                                locationItems?.length < 5
                                  ? 'Select up to 5 locations for this job posting.'
                                  : "You've selected the maximum of 5 locations. Remove one to add another."
                              }
                            >
                              <Icon icon={regular('info-circle')} />
                            </span>
                          </InfoWrapper>
                        }
                        for="locationAreas"
                        error={
                          touched.locationAreas && !!errors.locationAreas ? (
                            <>{errors.locationAreas as String}</>
                          ) : null
                        }
                      >
                        <DSSelectMultiple
                          placeholder="Select up to 5 locations"
                          selectedItemIds={locationItems}
                          withSearchBar={true}
                          searchBarPlaceholder="Type to find location"
                          onChange={(items) => {
                            if (locationItems?.length < 6) {
                              setLocationItems(items ?? []);
                              setFieldValue('locationAreas', items ?? []);
                            }
                          }}
                          maxSelectedItems={5}
                          items={REMOTE_LOCATION_OPTIONS}
                          topItems={10}
                          groupBy="group"
                        />
                      </DSField>
                    )}

                    {values.workplaceType &&
                      values.workplaceType !== TWorkplaceType.Remote && (
                        <DSField
                          label="Office Location"
                          for="locationCity"
                          error={
                            touched.locationCity && !!errors.locationCity ? (
                              <>{errors.locationCity as String}</>
                            ) : null
                          }
                        >
                          <DSCombobox
                            placeholder="Type to find city"
                            filterBy="label"
                            labelField="label"
                            alwaysShowInputValue
                            hideDropdownOnEmptyResults
                            selectedItemId={locationId}
                            items={comboboxItems}
                            onChange={(location: IComboboxItem) => {
                              setFieldValue('locationCity', location.label);
                              if (location.id !== 'default') {
                                setLocationId(location.id);
                              }
                            }}
                            error={
                              touched.locationCity && !!errors.locationCity
                            }
                            onSearchChange={onSearchChange}
                            footer={
                              comboboxItems.length !== 0 &&
                              !(
                                comboboxItems.length === 1 &&
                                comboboxItems[0].id === 'default'
                              ) && (
                                <ComboboxFooter>
                                  <img src={googleOnWhite} />
                                </ComboboxFooter>
                              )
                            }
                          />
                        </DSField>
                      )}
                  </SettingsSection>

                  <SettingsSection>
                    <DSField
                      required
                      label="Job description"
                      for="description"
                      data-has-error={
                        !!(errors.description && touched.description)
                      }
                      error={
                        touched.description && !!errors.description ? (
                          <>{errors.description as String}</>
                        ) : null
                      }
                    >
                      <TextEditor
                        state={editorState}
                        onChange={(state) => {
                          setFieldValue(
                            'description',
                            getEditorMarkdown(state)
                          );
                          setEditorState(state);
                        }}
                        lineHeight={1.71}
                        placeholder="Describe the role, key responsibilities, required skills, and what makes this opportunity unique..."
                        error={touched.description && !!errors.description}
                        renderAboveEditorContent={() => (
                          <EditorActions
                            workspaceId={String(workspace.id)}
                            jobOpeningId={jobOpeningId}
                            onEditorUpdated={(description) => {
                              const state = createEditorState(description);
                              setFieldValue(
                                'description',
                                getEditorMarkdown(state)
                              );
                              setEditorState(state);
                            }}
                          />
                        )}
                      />
                    </DSField>
                  </SettingsSection>
                </DSLandingPage.Main>
              </DSLandingPage>
            </Container>
          </StyledForm>
        );
      }}
    </Formik>
  );
}

const StyledForm = styled(Form)`
  display: flex;
  flex: 1;
`;

const Container = styled.div`
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const SaveButton = styled(DSButton)`
  float: right;
`;

const SettingsSection = styled.section`
  display: grid;
  gap: 16px;
  color: ${(props) => props.theme.typography.colorPrimary};
`;

const OptionsWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: '1fr 1fr 1fr';
  gap: 24px;
`;

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Info = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => props.theme.typography.colorSecondary};
`;

const ColumnWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 50%;
`;

const SalaryInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: flex-start;

  > * {
    flex: 1;
  }
`;

const HeaderWrapper = styled.div<{ offsetTopBanner: number }>`
  display: grid;
  grid-template-columns: 1fr minmax(0, max-content);
  align-items: center;
  position: sticky;
  top: ${({ offsetTopBanner }) =>
    offsetTopBanner + 125}px; // page header height
  z-index: 4;
  margin-top: -16px; // to cover the padding under the header
  margin-bottom: 16px;
  padding: 16px;
  background: ${(props) => props.theme.colors.white};
  box-shadow: 0px 1px 4px rgba(44, 19, 56, 0.1);
  margin-left: -16px;
  margin-right: -16px;
  width: calc(100% + 32px);

  @media all and (min-width: 768px) {
    margin-left: -24px;
    margin-right: -24px;
    width: calc(100% + 48px);
    padding-left: 24px;
    padding-right: 24px;
  }
`;

const SaveInfo = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => props.theme.typography.colorSecondary};
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: flex-start;
  cursor: pointer;
`;

const ComboboxFooter = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  justify-content: right;
  padding: 4px 12px 8px;
`;

const TextEditor = styled(CustomTextEditor)`
  & > :first-child {
    padding: 8px 12px;
  }
  & > *:not(:first-child) {
    margin-top: 12px;
    padding: 0 12px;

    div.ProseMirror-widget {
      left: 12px;
    }
  }
  & > :last-child {
    padding: 0 12px 12px;
  }
`;
